// Contas das páginas "Faturamento 12m" e "Margem por setor" (sem tela, sem antd).
// Tudo aqui é puro: recebe o JSON do servidor e devolve o que o gráfico e a tabela desenham.
import { chaveCanonica, ordenarSetores } from '../../../utils/setoresDre';
import { CORTE_ANALISE } from '../../../utils/avisosResultadoContrato';
import type { Acumulado, FatCliente, FatMes, MesDre, ValoresLinha, DreSetores } from './apiDre';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** "2026/03" (ou "2026-03") vira { ano: 2026, mes: 3 }; inválido vira null. */
export function partesDoPeriodo(periodo: string): { ano: number; mes: number } | null {
  const m = /^(\d{4})[/-](\d{2})/.exec(periodo || '');
  if (!m) return null;
  const mes = Number(m[2]);
  return mes >= 1 && mes <= 12 ? { ano: Number(m[1]), mes } : null;
}

/** "2026/03" vira "mar/26" (rótulo do eixo). */
export function rotuloMesCurto(periodo: string): string {
  const p = partesDoPeriodo(periodo);
  return p ? `${MESES[p.mes - 1]}/${String(p.ano).slice(2)}` : periodo;
}

/** "2026/03" vira "03/2026" (texto corrido). */
export function rotuloMes(periodo: string): string {
  const p = partesDoPeriodo(periodo);
  return p ? `${String(p.mes).padStart(2, '0')}/${p.ano}` : periodo;
}

export const periodoDe = (ano: number, mes: number): string => `${ano}/${String(mes).padStart(2, '0')}`;

/** Mês de "hoje" ("AAAA-MM-DD") no formato do servidor ("AAAA/MM"). */
export function periodoDeHoje(hoje: string): string | undefined {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(hoje || '');
  return m ? `${m[1]}/${m[2]}` : undefined;
}

/** Todos os meses de `de` até `ate`, inclusive ("AAAA/MM"). */
export function listaMeses(de: string, ate: string): string[] {
  const a = partesDoPeriodo(de), b = partesDoPeriodo(ate);
  if (!a || !b) return [];
  const out: string[] = [];
  let ano = a.ano, mes = a.mes;
  while ((ano < b.ano || (ano === b.ano && mes <= b.mes)) && out.length < 240) {
    out.push(periodoDe(ano, mes));
    mes += 1;
    if (mes > 12) { mes = 1; ano += 1; }
  }
  return out;
}

/** Pedaços de até `tamanho` meses (a reserva para quando uma chamada só demora demais). */
export function blocosDeMeses(de: string, ate: string, tamanho = 3): { de: string; ate: string }[] {
  const meses = listaMeses(de, ate);
  const blocos: { de: string; ate: string }[] = [];
  for (let i = 0; i < meses.length; i += tamanho) {
    const fatia = meses.slice(i, i + tamanho);
    blocos.push({ de: fatia[0], ate: fatia[fatia.length - 1] });
  }
  return blocos;
}

const num = (v: any): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Junta chaves curtas e canônicas do mesmo setor e tira "total". */
export function porSetorCanonico(obj: Record<string, number> | undefined | null): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (k === 'total') continue;
    const c = chaveCanonica(k);
    out[c] = (out[c] || 0) + num(v);
  }
  return out;
}

/** Setores a desenhar: os que o servidor listou mais qualquer outro que apareça nos números. */
export function setoresDaResposta(listados: string[] | undefined, mapas: (Record<string, number> | undefined)[]): string[] {
  const chaves: string[] = [...(listados || [])];
  mapas.forEach((m) => Object.keys(m || {}).forEach((k) => { if (k !== 'total') chaves.push(k); }));
  return ordenarSetores(chaves);
}

// ── Faturamento ──────────────────────────────────────────────────────────────
export interface LinhaFaturamento {
  periodo: string; label: string; parcial: boolean; total: number; fora_da_dre: number;
  [setor: string]: number | string | boolean;
}

export function linhasFaturamento(meses: FatMes[], setores: string[]): LinhaFaturamento[] {
  return meses.map((m) => {
    const ps = porSetorCanonico(m.por_setor);
    const linha: LinhaFaturamento = {
      periodo: m.periodo, label: rotuloMesCurto(m.periodo), parcial: !!m.parcial,
      total: num(m.total), fora_da_dre: num(m.fora_da_dre),
    };
    setores.forEach((s) => { linha[s] = ps[s] || 0; });
    return linha;
  });
}

/** Maiores N clientes mais um resumo do resto ("Demais"). */
export function clientesTop(clientes: FatCliente[], n = 15): {
  top: FatCliente[]; demais: { n: number; total: number; pct: number } | null;
} {
  const ordenados = [...clientes].sort((a, b) => num(b.total) - num(a.total) || num(a.posicao) - num(b.posicao));
  const top = ordenados.slice(0, n);
  const resto = ordenados.slice(n);
  if (!resto.length) return { top, demais: null };
  return {
    top,
    demais: {
      n: resto.length,
      total: resto.reduce((s, c) => s + num(c.total), 0),
      pct: resto.reduce((s, c) => s + num(c.pct), 0),
    },
  };
}

// ── Margem por setor ─────────────────────────────────────────────────────────
export type ModoMargem = 'mes' | 'acumulado';

export interface LinhaGraficoMargem {
  periodo: string; label: string; parcial: boolean; total: number;
  [setor: string]: number | string | boolean;
}

/** Soma de uma linha da DRE em vários meses (usada quando o servidor não manda o acumulado). */
export function somarLinha(meses: MesDre[], linha: string): ValoresLinha {
  const out: ValoresLinha = { total: 0 };
  meses.forEach((m) => {
    const v = m.valores?.[linha];
    if (!v) return;
    Object.entries(v).forEach(([k, x]) => {
      const c = k === 'total' ? 'total' : chaveCanonica(k);
      out[c] = (out[c] || 0) + num(x);
    });
  });
  return out;
}

/** Categorias (linha_dre) de um grupo da DRE somadas nos meses, setor a setor (chaves canônicas + total). Mês sem a categoria conta 0. */
export function categoriasAcumuladas(meses: MesDre[], chave: string): Record<string, ValoresLinha> {
  const out: Record<string, ValoresLinha> = {};
  meses.forEach((m) => {
    const cats = m.categorias?.[chave];
    if (!cats) return;
    Object.entries(cats).forEach(([cat, v]) => {
      const alvo = (out[cat] = out[cat] || { total: 0 });
      Object.entries(v || {}).forEach(([k, x]) => {
        const c = k === 'total' ? 'total' : chaveCanonica(k);
        alvo[c] = (alvo[c] || 0) + num(x);
      });
    });
  });
  return out;
}

/** Categorias de um grupo, para UM setor: valor de cada mês (chave = periodo) e do período todo (chave "periodo"). */
export function categoriasPorMes(meses: MesDre[], chave: string, setor: string): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  meses.forEach((m) => {
    const cats = m.categorias?.[chave];
    if (!cats) return;
    Object.entries(cats).forEach(([cat, v]) => {
      const alvo = (out[cat] = out[cat] || { periodo: 0 });
      const x = porSetorCanonico(v)[setor] || 0;
      alvo[m.periodo] = (alvo[m.periodo] || 0) + x;
      alvo.periodo += x;
    });
  });
  meses.forEach((m) => Object.values(out).forEach((a) => { if (a[m.periodo] == null) a[m.periodo] = 0; }));
  return out;
}

export function valoresAcumulados(dados: Pick<DreSetores, 'meses' | 'acumulado'>, linha: string): ValoresLinha {
  const doServidor = dados.acumulado?.valores?.[linha];
  if (!doServidor) return somarLinha(dados.meses, linha);
  const out: ValoresLinha = { total: num(doServidor.total) };
  Object.entries(doServidor).forEach(([k, x]) => {
    if (k === 'total') return;
    const c = chaveCanonica(k);
    out[c] = (out[c] || 0) + num(x);
  });
  return out;
}

/** Linhas do gráfico de barras empilhadas: lucro por setor mais o total da empresa (= DRE). */
export function linhasMargem(dados: Pick<DreSetores, 'meses' | 'acumulado'>, linha: string, setores: string[], modo: ModoMargem): LinhaGraficoMargem[] {
  if (modo === 'acumulado') {
    const v = valoresAcumulados(dados, linha);
    const l: LinhaGraficoMargem = { periodo: 'acumulado', label: 'Período', parcial: dados.meses.some((m) => m.parcial), total: num(v.total) };
    setores.forEach((s) => { l[s] = num(v[s]); });
    return [l];
  }
  return dados.meses.map((m) => {
    const v = m.valores?.[linha] || {};
    const ps = porSetorCanonico(v);
    const l: LinhaGraficoMargem = { periodo: m.periodo, label: rotuloMesCurto(m.periodo), parcial: !!m.parcial, total: num(v.total) };
    setores.forEach((s) => { l[s] = ps[s] || 0; });
    return l;
  });
}

/** Margem % = lucro dividido pela receita bruta do setor; receita zero ou negativa vira null. */
export function margemPct(lucro: number, receita: number): number | null {
  return receita > 0 ? (lucro / receita) * 100 : null;
}

/** Qual linha é a Receita bruta (o servidor lista as linhas na ordem da DRE). */
export function chaveReceita(linhas: { chave: string }[]): string {
  const achada = linhas.find((l) => l.chave === 'receita_bruta') || linhas.find((l) => /receita_bruta|^receitas?$/.test(l.chave));
  return achada ? achada.chave : 'receita_bruta';
}

// ── Avisos ───────────────────────────────────────────────────────────────────
/** Primeiro mês cujo fechamento pode ser refeito com a regra de mão de obra de hoje —
 *  o corte da análise do motor (2025/01 desde 15/10/2026, quando 2025 foi descongelado). */
export const PRIMEIRO_MES_RECALCULAVEL = CORTE_ANALISE;

export interface AvisosMeses {
  congeladas: string[]; antigas: string[]; semFechamento: string[]; abertos: string[];
  /** Meses em que o fechamento pode ser refeito (a partir do corte da análise). */
  recalculaveis: string[];
  /** Meses cujo fechamento é anterior à informação de setor do custo (ou alguém mudou o setor depois). */
  setoresDesatualizados: string[];
  /** Meses em que a soma dos setores não bate com a DRE, com a maior diferença. */
  conferenciaErrada: { periodo: string; diferenca: number }[];
}

export function avisosDosMeses(meses: MesDre[]): AvisosMeses {
  const a: AvisosMeses = { congeladas: [], antigas: [], semFechamento: [], abertos: [], recalculaveis: [], setoresDesatualizados: [], conferenciaErrada: [] };
  meses.forEach((m) => {
    const r = m.fechamento?.regra;
    if (r === 'congelada') a.congeladas.push(m.periodo);
    if (r === 'antiga') a.antigas.push(m.periodo);
    if (r === 'sem_fechamento') a.semFechamento.push(m.periodo);
    if (r === 'setores_desatualizados') a.setoresDesatualizados.push(m.periodo);
    if ((r === 'antiga' || r === 'sem_fechamento' || r === 'setores_desatualizados') && m.periodo >= PRIMEIRO_MES_RECALCULAVEL) a.recalculaveis.push(m.periodo);
    if (m.parcial) a.abertos.push(m.periodo);
    const maior = Object.values(m.conferencia || {}).reduce((mx: number, v) => (Math.abs(num(v)) > Math.abs(mx) ? num(v) : mx), 0);
    if (Math.abs(maior) >= 0.01) a.conferenciaErrada.push({ periodo: m.periodo, diferenca: maior });
  });
  return a;
}

/** Rótulo da regra do fechamento do mês; regra desconhecida NUNCA vira "atual" (mostra o texto cru). */
export function rotuloRegra(regra: string | undefined | null): string {
  switch (regra) {
    case 'atual': return 'Atual';
    case 'antiga': return 'Regra antiga de mão de obra';
    case 'congelada': return 'Congelado';
    case 'sem_fechamento': return 'Sem fechamento calculado';
    case 'setores_desatualizados': return 'Setor ou rateio de alguém mudou — recalcule';
    default: return regra ? `Regra desconhecida (${regra})` : 'Sem fechamento calculado';
  }
}

/** Quantas linhas tem uma pendência (número puro, { n } ou lista de itens). */
export function contagemPendencia(v: any): number {
  if (typeof v === 'number') return v;
  if (v && typeof v.n === 'number') return v.n;
  return itensDaPendencia(v).length;
}

export function totalMaoObraParada(meses: MesDre[]): number {
  return meses.reduce((s, m) => s + num(m.mao_obra_parada), 0);
}

/** 15/10/2026 — a parte da mão de obra parada que ficou no restante (repartida pela receita):
 *  ficha sem setor ou lançamento sem pessoa. O resto foi para o setor da ficha da pessoa.
 *  Servidor antigo (sem o campo) → tudo no restante. */
export function totalMaoObraParadaNoRestante(meses: MesDre[]): number {
  return meses.reduce((s, m) => s + num(m.mao_obra_parada_no_restante ?? m.mao_obra_parada), 0);
}

/** Receita "sem setor" na janela (linha de receita bruta, coluna sem_setor). */
export function totalSemSetor(meses: MesDre[], linhaReceita: string): number {
  return meses.reduce((s, m) => s + num(m.valores?.[linhaReceita]?.sem_setor), 0);
}

// ── Reserva em blocos ────────────────────────────────────────────────────────
/** Chave que identifica o mesmo item em blocos diferentes (contrato, parcela ou lançamento). */
function chaveDoItem(x: any): string {
  if (x && typeof x === 'object') {
    if (x.escopo != null && x.id != null) return `a:${x.id}`;                                            // aceite registrado
    if (x.tipo != null) return `t:${x.tipo}:${x.ref ?? ''}|${x.contrato_id ?? ''}|${x.linha ?? ''}`;      // custo por quem resolve
    const id = x.contrato_id ?? x.parcela_id ?? x.id;
    if (id != null) return `${x.contrato_id != null ? 'c' : x.parcela_id != null ? 'p' : 'i'}:${id}`;
  }
  return JSON.stringify(x);
}

/** Junta a mesma pendência de vários blocos: itens sem repetir (o valor do mesmo contrato soma), n e valor refeitos. */
export function juntarPendencia(partes: any[]): any {
  const lista = partes.filter((v) => v != null);
  if (!lista.length) return undefined;
  const formaLista = lista.every(Array.isArray);
  const itens = new Map<string, any>();
  let n = 0, valor = 0, repetidos = 0;
  lista.forEach((v) => {
    const its = itensDaPendencia(v);
    n += Array.isArray(v) ? its.length : num(v.n) || its.length;
    valor += Array.isArray(v) ? its.reduce((s, x) => s + num(x?.valor), 0) : num(v.valor);
    its.forEach((x) => {
      const c = chaveDoItem(x);
      const antes = itens.get(c);
      if (!antes) { itens.set(c, x); return; }
      repetidos += 1;
      if (x && typeof x === 'object' && typeof x.valor === 'number') itens.set(c, { ...antes, valor: num(antes.valor) + x.valor });
    });
  });
  const itensJuntos = Array.from(itens.values());
  // Contratos vêm do mais negativo para o menos; o resto fica na ordem em que chegou.
  if (itensJuntos.length && itensJuntos.every((x) => x && typeof x === 'object' && x.contrato_id != null)) {
    itensJuntos.sort((a, b) => num(a.valor) - num(b.valor));
  }
  if (formaLista) return itensJuntos;
  return { n: n - repetidos, valor: Math.round(valor * 100) / 100, itens: itensJuntos };
}

/** Junta respostas de blocos (ex.: trimestres) numa só, refazendo o acumulado pela soma dos meses. */
export function juntarBlocos(blocos: DreSetores[]): DreSetores {
  if (!blocos.length) throw new Error('Nenhum bloco para juntar');
  const meses = blocos.flatMap((b) => b.meses).sort((a, b) => a.periodo.localeCompare(b.periodo));
  const chavesLinha = blocos[0].linhas.map((l) => l.chave);
  const valores: Record<string, ValoresLinha> = {};
  chavesLinha.forEach((k) => { valores[k] = somarLinha(meses, k); });

  // Margem do período = lucro dividido pela receita bruta, setor a setor.
  const rec = valores[chaveReceita(blocos[0].linhas)] || { total: 0 };
  const margens: NonNullable<Acumulado['margens']> = {};
  (['lucro_bruto', 'resultado_op', 'lucro_liq', 'lucro_final'] as const).forEach((k) => {
    const v = valores[k];
    if (!v) return;
    const m: Record<string, number | null> = {};
    Object.keys(v).forEach((s) => { m[s] = margemPct(num(v[s]), num(rec[s])); });
    margens[k] = m;
  });

  // Pendências: cada bloco manda { n, valor, itens } (ou uma lista simples); junta sem repetir.
  const pendencias: Record<string, any> = {};
  const nomes = new Set<string>();
  blocos.forEach((b) => Object.keys(b.pendencias || {}).forEach((k) => nomes.add(k)));
  nomes.forEach((k) => { pendencias[k] = juntarPendencia(blocos.map((b) => b.pendencias?.[k])); });

  const primeiro = blocos[0], ultimo = blocos[blocos.length - 1];
  return {
    ...ultimo,
    de: primeiro.de, ate: ultimo.ate,
    setores: ordenarSetores(blocos.flatMap((b) => b.setores)),
    meses,
    acumulado: { valores, margens },
    pendencias,
    ms: blocos.reduce((s, b) => s + num(b.ms), 0),
  };
}

// ── Formatação e janela ──────────────────────────────────────────────────────
/** R$ 1.234 (sem centavos; o sinal aparece só quando negativo). */
export const reais = (v: number): string =>
  (v < 0 ? '-' : '') + 'R$ ' + Math.abs(num(v)).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

/** Rótulo curto do eixo: 1,2 mi, 340 mil, 800. */
export function reaisCurto(v: number): string {
  const a = Math.abs(num(v)), s = v < 0 ? '-' : '';
  if (a >= 1e6) return `${s}${(a / 1e6).toFixed(1).replace('.', ',')} mi`;
  if (a >= 1e3) return `${s}${Math.round(a / 1e3)} mil`;
  return `${s}${Math.round(a)}`;
}

export const pctTexto = (v: number | null | undefined, casas = 1): string =>
  v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(casas).replace('.', ',')}%`;

/** Anda `n` meses (negativo volta) a partir de "AAAA/MM". */
export function deslocarMes(periodo: string, n: number): string {
  const p = partesDoPeriodo(periodo);
  if (!p) return periodo;
  const total = p.ano * 12 + (p.mes - 1) + n;
  return periodoDe(Math.floor(total / 12), (total % 12) + 1);
}

/** Os 12 meses fechados até "hoje" (AAAA-MM-DD): do mês de hoje menos 12 ao mês de hoje menos 1. */
export function janelaPadrao(hoje: string): { de: string; ate: string } | null {
  const atual = periodoDeHoje(hoje);
  return atual ? { de: deslocarMes(atual, -12), ate: deslocarMes(atual, -1) } : null;
}

// ── Filtro de período ────────────────────────────────────────────────────────
/** Até quantos meses cada rota aceita por chamada (acima disso o servidor responde 400). */
export const MAX_MESES_FATURAMENTO = 36;
export const MAX_MESES_MARGEM = 24;

export type ModoPeriodo = 'ultimos12' | 'este_ano' | 'ano_passado' | 'personalizado';
export interface FiltroPeriodo {
  modo: ModoPeriodo;
  /** Mês em andamento entra (vale para "últimos 12", "este ano" e "personalizado"). */
  incluirAtual: boolean;
  /** Só no personalizado: "AAAA/MM". */
  de?: string | null;
  ate?: string | null;
}
/** "2026-03" ou "2026/03" → "2026/03"; inválido → null. */
function normalizarPeriodo(v: string | null | undefined): string | null {
  const p = partesDoPeriodo(v || '');
  return p ? periodoDe(p.ano, p.mes) : null;
}

export type JanelaFiltro =
  | { ok: true; de: string; ate: string; meses: number; aviso: string | null }
  | { ok: false; aviso: string };

/**
 * Converte o filtro da tela em `de`/`ate` para a rota. Nunca devolve janela maior que
 * `maxMeses`: corta os meses mais antigos e avisa (o servidor recusaria com 400).
 * - últimos 12: fechados = mês atual −12 a −1; com o mês atual = −11 a 0;
 * - este ano: janeiro até o último mês fechado (ou o atual); em janeiro, sem o atual, não há mês;
 * - ano passado: janeiro a dezembro do ano anterior (o mês atual não se aplica);
 * - personalizado: o que a pessoa escolheu, sem passar do último mês permitido.
 */
export function janelaDoFiltro(f: FiltroPeriodo, hoje: string, maxMeses: number): JanelaFiltro {
  const atual = periodoDeHoje(hoje);
  if (!atual) return { ok: false, aviso: 'Não consegui saber a data de hoje.' };
  const p = partesDoPeriodo(atual)!;
  const ultimo = f.incluirAtual ? atual : deslocarMes(atual, -1);
  let de: string, ate: string;
  let aviso: string | null = null;
  if (f.modo === 'ultimos12') {
    ate = ultimo; de = deslocarMes(ultimo, -11);
  } else if (f.modo === 'este_ano') {
    de = periodoDe(p.ano, 1); ate = ultimo;
    if (ate < de) return { ok: false, aviso: `Nenhum mês de ${p.ano} fechou ainda. Marque “incluir mês atual” para ver ${rotuloMes(atual)}.` };
  } else if (f.modo === 'ano_passado') {
    de = periodoDe(p.ano - 1, 1); ate = periodoDe(p.ano - 1, 12);
  } else {
    const a = normalizarPeriodo(f.de), b = normalizarPeriodo(f.ate);
    if (!a || !b) return { ok: false, aviso: 'Escolha o mês inicial e o mês final.' };
    if (a > b) return { ok: false, aviso: 'O mês inicial está depois do mês final.' };
    de = a; ate = b;
    if (ate > ultimo) {
      ate = ultimo;
      aviso = f.incluirAtual
        ? `Meses depois de ${rotuloMes(ultimo)} ainda não existem e ficaram de fora.`
        : `Mostrando até ${rotuloMes(ultimo)}: ${rotuloMes(atual)} ainda está em andamento (marque “incluir mês atual” para ver).`;
      if (de > ate) return { ok: false, aviso };
    }
  }
  let meses = listaMeses(de, ate).length;
  if (meses > maxMeses) {
    de = deslocarMes(ate, -(maxMeses - 1));
    meses = maxMeses;
    aviso = `Esta tela mostra no máximo ${maxMeses} meses: mostrando de ${rotuloMes(de)} a ${rotuloMes(ate)}.`;
  }
  return { ok: true, de, ate, meses, aviso };
}

/** Meses que o filtro personalizado oferece: do mais novo (mês atual) ao mais antigo (`desde`). */
export function opcoesDeMeses(hoje: string, desde = '2020/01'): { value: string; label: string }[] {
  const atual = periodoDeHoje(hoje);
  if (!atual) return [];
  return listaMeses(desde, atual).reverse().map((p) => ({ value: p, label: `${rotuloMes(p)} · ${rotuloMesCurto(p).slice(0, 3)}` }));
}

// ── Linha de tendência ───────────────────────────────────────────────────────
export interface Tendencia {
  /** Quanto a série sobe (ou desce) por mês, em reais. */
  porMes: number;
  /** Valor da reta em cada mês da série (inclusive os que ficaram fora da conta). */
  pontos: number[];
}

/**
 * Reta de mínimos quadrados sobre os meses mostrados (x = 0, 1, 2…). `null` num mês = ele
 * não entra na conta (ex.: mês em andamento, que puxaria a reta para baixo), mas a reta
 * continua desenhada nele. Menos de 2 meses na conta = sem tendência.
 */
export function tendenciaLinear(valores: (number | null)[]): Tendencia | null {
  const xs: number[] = [], ys: number[] = [];
  valores.forEach((v, i) => { if (v != null && Number.isFinite(v)) { xs.push(i); ys.push(v); } });
  const n = xs.length;
  if (n < 2) return null;
  const mx = xs.reduce((s, x) => s + x, 0) / n;
  const my = ys.reduce((s, y) => s + y, 0) / n;
  let sxy = 0, sxx = 0;
  for (let i = 0; i < n; i++) { sxy += (xs[i] - mx) * (ys[i] - my); sxx += (xs[i] - mx) ** 2; }
  const porMes = sxx === 0 ? 0 : sxy / sxx;
  const base = my - porMes * mx;
  return { porMes, pontos: valores.map((_, i) => base + porMes * i) };
}

/** "+R$ 12.345/mês" (ou "-R$ …"). */
export const tendenciaTexto = (porMes: number): string => `${porMes >= 0 ? '+' : ''}${reais(porMes)}/mês`;

// ── Série mensal (empresa ou um cliente) e o que o gráfico desenha ───────────
export interface MesSerie { periodo: string; parcial: boolean; porSetor: Record<string, number>; total: number }

export function serieDaEmpresa(meses: FatMes[]): MesSerie[] {
  return meses.map((m) => ({ periodo: m.periodo, parcial: !!m.parcial, porSetor: porSetorCanonico(m.por_setor), total: num(m.total) }));
}

/**
 * Evolução de um cliente nos meses da resposta, a partir de `por_mes_setor`. Servidor antigo
 * (sem `por_mes_setor`) devolve só o total do mês (`porSetor` vazio) — a tela avisa.
 */
export function serieDoCliente(c: Pick<FatCliente, 'por_mes' | 'por_mes_setor'>, meses: { periodo: string; parcial?: boolean }[]): MesSerie[] {
  return meses.map((m) => {
    const porSetor = porSetorCanonico(c.por_mes_setor?.[m.periodo]);
    const somaSetores = Object.values(porSetor).reduce((s, v) => s + v, 0);
    const total = c.por_mes_setor ? somaSetores : num(c.por_mes?.[m.periodo]);
    return { periodo: m.periodo, parcial: !!m.parcial, porSetor, total };
  });
}

export interface LinhaGrafico {
  periodo: string; label: string; parcial: boolean;
  /** O valor em foco no mês: o do setor escolhido, ou a soma de todos. */
  total: number;
  tendencia?: number;
  [setor: string]: number | string | boolean | undefined;
}

/**
 * Linhas do gráfico de barras: com `setor`, só a barra dele (e `total` = o valor dele);
 * sem, todas. Com `comTendencia`, a reta do `total` (mês parcial fora da conta).
 */
export function linhasDoGrafico(serie: MesSerie[], setores: string[], setor: string | null, comTendencia: boolean): {
  linhas: LinhaGrafico[]; tendencia: Tendencia | null;
} {
  const mostrar = setor ? [setor] : setores;
  const linhas: LinhaGrafico[] = serie.map((m) => {
    const l: LinhaGrafico = {
      periodo: m.periodo, label: rotuloMesCurto(m.periodo) + (m.parcial ? '*' : ''), parcial: m.parcial,
      total: setor ? (m.porSetor[setor] || 0) : m.total,
    };
    mostrar.forEach((s) => { l[s] = m.porSetor[s] || 0; });
    return l;
  });
  const tendencia = comTendencia ? tendenciaLinear(linhas.map((l) => (l.parcial ? null : l.total))) : null;
  if (tendencia) linhas.forEach((l, i) => { l.tendencia = tendencia.pontos[i]; });
  return { linhas, tendencia };
}

/** Composição por setor de uma série (Σ dos meses), maior primeiro, com % sobre o total. */
export function composicaoPorSetor(serie: MesSerie[], setores: string[]): { setor: string; valor: number; pct: number | null }[] {
  const soma: Record<string, number> = {};
  serie.forEach((m) => Object.entries(m.porSetor).forEach(([s, v]) => { soma[s] = (soma[s] || 0) + v; }));
  const total = serie.reduce((s, m) => s + m.total, 0);
  return ordenarSetores([...setores, ...Object.keys(soma)])
    .map((s) => ({ setor: s, valor: soma[s] || 0, pct: total ? ((soma[s] || 0) / total) * 100 : null }))
    .filter((x) => Math.abs(x.valor) >= 0.5);
}

/** Série mensal de um setor: valor e % sobre o total do mês. */
export function serieDoSetor(serie: MesSerie[], setor: string): { periodo: string; parcial: boolean; valor: number; pct: number | null }[] {
  return serie.map((m) => {
    const valor = m.porSetor[setor] || 0;
    return { periodo: m.periodo, parcial: m.parcial, valor, pct: m.total ? (valor / m.total) * 100 : null };
  });
}

/**
 * Clientes ranqueados só pelo que faturaram num setor: total, %, % acumulado e posição
 * refeitos sobre o total do setor (quem não faturou nele sai da lista).
 */
export function clientesDoSetor(clientes: FatCliente[], setor: string): FatCliente[] {
  const com = clientes
    .map((c) => ({ c, v: porSetorCanonico(c.por_setor)[setor] || 0 }))
    .filter((x) => Math.abs(x.v) >= 0.5)
    .sort((a, b) => b.v - a.v);
  const total = com.reduce((s, x) => s + x.v, 0);
  let acum = 0;
  return com.map((x, i) => {
    const pct = total ? (x.v / total) * 100 : 0;
    acum += pct;
    return { ...x.c, total: x.v, pct, pct_acumulado: acum, posicao: i + 1 };
  });
}

/** Data/hora ISO vira dd/mm/aaaa no fuso de Brasília. */
export function dataBr(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

/** Quando foi calculada a divisão dos custos do mês MAIS ANTIGO da janela (o pior caso). */
export function calculadoEmMaisAntigo(meses: MesDre[]): string | null {
  const datas = meses.map((m) => m.fechamento?.calculado_em).filter((x): x is string => !!x).sort();
  return datas.length ? datas[0] : null;
}

const ROTULO_LINHA: Record<string, string> = {
  receita_bruta: 'Receita bruta',
  deducoes: 'Deduções',
  rec_liq_comercial: 'Receita líquida',
  comissoes: 'Comissões',
  custos_operacionais: 'Custos operacionais',
  custos_mao_obra: 'Mão de obra',
  custos_outros: 'Outros custos',
  lucro_bruto: 'Lucro bruto',
  despesas_comerciais: 'Despesas comerciais',
  despesas_administrativas: 'Despesas administrativas',
  outras_despesas_operacionais: 'Outras despesas operacionais',
  outras_receitas_operacionais: 'Outras receitas operacionais',
  estorno: 'Estornos',
  resultado_op: 'Resultado operacional',
  despesas_financeiras: 'Despesas financeiras',
  receitas_financeiras: 'Receitas financeiras',
  resultado_antes: 'Resultado antes dos impostos',
  impostos: 'Impostos',
  lucro_liq: 'Lucro líquido',
  investimentos: 'Investimentos',
  lucro_final: 'Lucro final',
};

export function rotuloLinha(chave: string, rotuloDoServidor?: string): string {
  if (rotuloDoServidor) return rotuloDoServidor;
  if (ROTULO_LINHA[chave]) return ROTULO_LINHA[chave];
  const t = chave.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Lista de uma pendência: o servidor pode mandar a lista direto ou { n, itens }. */
export function itensDaPendencia(v: any): any[] {
  if (Array.isArray(v)) return v;
  if (v && Array.isArray(v.itens)) return v.itens;
  return [];
}

/** Texto curto de um item de pendência (contrato, parcela, lançamento) sem depender do formato exato. */
export function rotuloItem(item: any): string {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item);
  const nome = item.contrato_numero || item.numero || item.contrato || item.codigo || item.titulo || item.nome || item.descricao || item.id;
  const cliente = item.cliente || item.cliente_nome;
  const valor = typeof item.valor === 'number' ? ` · ${reais(item.valor)}` : '';
  const base = [nome, cliente].filter((x) => x != null && x !== '').join(' · ');
  return (base || JSON.stringify(item)) + valor;
}
